import { CloseOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

const { Text } = Typography;

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);

export const isPreviewableImage = (name: string | null | undefined): boolean => {
  if (!name) return false;
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS.has(ext);
};

export interface PreviewDoc {
  name: string;
  src: string;
  contentType?: string | null;
}

interface IProps {
  doc: PreviewDoc;
  /** Fixed width when used as a side-by-side pane; omit to fill the parent. */
  width?: number;
  onClose?: () => void;
}

/**
 * Document viewer (zoomable image or embedded PDF) used both as the side-by-side pane inside a
 * drawer — see the inventory item / movement drawers — and as the body of DocumentPreviewDrawer.
 */
export const DocumentPreviewPane: React.FC<IProps> = ({ doc, width, onClose }) => {
  const isImage = doc.contentType
    ? doc.contentType.toLowerCase().startsWith('image/')
    : isPreviewableImage(doc.name);

  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    setZoom(1);
  }, [doc.src]);
  const zoomOut = () => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)));

  // Keep the viewport centred on the same spot while zooming in.
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevZoomRef = useRef(1);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && zoom !== prevZoomRef.current && zoom > 1) {
      const ratio = zoom / Math.max(prevZoomRef.current, 1);
      el.scrollLeft = (el.scrollLeft + el.clientWidth / 2) * ratio - el.clientWidth / 2;
      el.scrollTop = (el.scrollTop + el.clientHeight / 2) * ratio - el.clientHeight / 2;
    }
    prevZoomRef.current = zoom;
  }, [zoom]);

  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const onPanStart = (e: React.MouseEvent) => {
    if (zoom === 1 || !scrollRef.current) return;
    e.preventDefault();
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: scrollRef.current.scrollLeft,
      top: scrollRef.current.scrollTop,
    };
    setIsDragging(true);
  };

  const onPanMove = (e: React.MouseEvent) => {
    if (!dragRef.current || !scrollRef.current) return;
    scrollRef.current.scrollLeft = dragRef.current.left - (e.clientX - dragRef.current.x);
    scrollRef.current.scrollTop = dragRef.current.top - (e.clientY - dragRef.current.y);
  };

  const onPanEnd = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <div
      style={{
        ...(width ? { width, flexShrink: 0, borderLeft: '1px solid #e8e8e8' } : { flex: 1, minWidth: 0 }),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: '#fafafa',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fff',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <Text
          strong
          style={{
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {doc.name}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {isImage && (
            <>
              <Button type="text" size="small" icon={<ZoomOutOutlined />} disabled={zoom <= 1} onClick={zoomOut} />
              <span style={{ fontSize: 12, color: '#8c8c8c', minWidth: 36, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </span>
              <Button type="text" size="small" icon={<ZoomInOutlined />} disabled={zoom >= 4} onClick={zoomIn} />
            </>
          )}
          {onClose && <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}
        </div>
      </div>

      <div
        ref={scrollRef}
        onMouseDown={onPanStart}
        onMouseMove={isDragging ? onPanMove : undefined}
        onMouseUp={onPanEnd}
        onMouseLeave={onPanEnd}
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 16,
          minHeight: 0,
          userSelect: isDragging ? 'none' : 'auto',
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          ...(zoom === 1 ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}),
        }}
      >
        {isImage ? (
          <img
            src={doc.src}
            alt={doc.name}
            draggable={false}
            style={
              zoom === 1
                ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }
                : { width: `${zoom * 100}%`, maxWidth: 'none', height: 'auto', display: 'block', pointerEvents: 'none' }
            }
          />
        ) : (
          <iframe
            src={doc.src}
            title={doc.name}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentPreviewPane;
